// src/pages/AIVideoDubbingPage.js
import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  PlayCircleOutline, 
  Language, 
  Mic, 
  Speed, 
  Security, 
  AttachMoney,
  CheckCircle,
  VideoLibrary,
  AutoAwesome
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../utils/color';
import useSEO from '../hooks/useSEO';
import Breadcrumbs from '../components/common/Breadcrumbs';

const BREADCRUMBS = [{ name: 'AI Video Dubbing', url: 'https://dublabs.ai/ai-video-dubbing' }];

const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${COLORS.background.primary} 0%, ${COLORS.background.secondary} 100%)`,
  padding: theme.spacing(8, 0),
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 30% 50%, ${COLORS.primary.main}15 0%, transparent 50%)`,
    pointerEvents: 'none',
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: `linear-gradient(135deg, ${COLORS.background.primary} 0%, ${COLORS.background.secondary} 100%)`,
  border: `1px solid ${COLORS.border.primary}`,
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 12px 24px ${COLORS.primary.main}20`,
    border: `1px solid ${COLORS.primary.main}30`,
  },
}));

const StatsCard = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  background: `linear-gradient(135deg, ${COLORS.primary.main}10, ${COLORS.secondary.main}10)`,
  borderRadius: theme.spacing(2),
  border: `1px solid ${COLORS.primary.main}20`,
}));

const AIVideoDubbingPage = () => {
  const navigate = useNavigate();
  
  // SEO optimization
  useSEO({
    title: 'AI Video Dubbing in 32 Languages with Perfect Lip-Sync',
    description: 'DubLabs dubs your videos into 32 languages with perfect lip-sync and voice cloning — 90% cheaper than studios. Contact us for a quote on your project.',
    canonical: 'https://dublabs.ai/ai-video-dubbing',
    breadcrumbs: BREADCRUMBS,
    dateModified: '2026-07-30',
  });

  const features = [
    {
      icon: <Language sx={{ fontSize: 40, color: COLORS.primary.main }} />,
      title: '32 Languages',
      description: 'Dub your videos into Spanish, French, German, Hindi, Japanese, and 27+ more languages with perfect accuracy.',
      details: ['Native speaker quality', 'Cultural context awareness', 'Accent preservation']
    },
    {
      icon: <PlayCircleOutline sx={{ fontSize: 40, color: COLORS.secondary.main }} />,
      title: 'Perfect Lip-Sync',
      description: 'Advanced AI technology ensures your lip movements match the dubbed audio perfectly in every language.',
      details: ['99.9% accuracy', 'Natural mouth movements', 'Real-time processing']
    },
    {
      icon: <Mic sx={{ fontSize: 40, color: COLORS.primary.light }} />,
      title: 'Voice Preservation',
      description: 'Maintain your unique voice characteristics and emotional tone across all languages.',
      details: ['Voice cloning technology', 'Emotion preservation', 'Tonal consistency']
    },
    {
      icon: <Speed sx={{ fontSize: 40, color: COLORS.secondary.light }} />,
      title: 'Lightning Fast',
      description: 'Process videos in minutes instead of weeks. 10x faster than traditional dubbing methods.',
      details: ['Minutes vs weeks', 'Batch processing', 'Real-time preview']
    },
    {
      icon: <AttachMoney sx={{ fontSize: 40, color: COLORS.primary.main }} />,
      title: '90% Cost Savings',
      description: 'Reduce dubbing costs by 90% compared to traditional voice actors and studios.',
      details: ['No studio fees', 'No talent costs', 'No equipment needed']
    },
    {
      icon: <Security sx={{ fontSize: 40, color: COLORS.secondary.main }} />,
      title: 'Enterprise Security',
      description: 'SOC 2 compliant platform with enterprise-grade security for your content.',
      details: ['SOC 2 Type II', 'End-to-end encryption', 'GDPR compliant']
    }
  ];

  const stats = [
    { number: '1000+', label: 'Happy Customers', color: COLORS.primary.main },
    { number: '50,000+', label: 'Videos Dubbed', color: COLORS.secondary.main },
    { number: '32+', label: 'Languages', color: COLORS.primary.light },
    { number: '99.9%', label: 'Accuracy', color: COLORS.secondary.light }
  ];

  const benefits = [
    'Reach global audiences instantly',
    'Maintain authentic voice and emotion',
    'Save 90% on dubbing costs',
    'Process videos 10x faster',
    'Perfect lip-sync technology',
    'Enterprise-grade security',
    'No technical expertise required',
    'Scalable for any volume'
  ];

  const handleGetStarted = () => {
    navigate('/contact');
  };

  return (
    <Box>
      <Breadcrumbs items={BREADCRUMBS} />
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  background: COLORS.gradients.primary,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                }}
              >
                AI Video Dubbing
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: COLORS.text.primary,
                  fontWeight: 600,
                  mb: 3,
                  fontSize: { xs: '1.5rem', md: '2rem' }
                }}
              >
                Transform Your Videos into 32 Languages
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: COLORS.text.secondary,
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                  mb: 4,
                  maxWidth: 500
                }}
              >
                Break language barriers with AI-powered video dubbing that preserves your voice, 
                emotion, and authenticity. Create multilingual content in minutes, not weeks.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGetStarted}
                  sx={{
                    background: COLORS.gradients.primary,
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 25px ${COLORS.primary.main}40`,
                    }
                  }}
                >
                  Get in Touch
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: COLORS.primary.main,
                    color: COLORS.primary.main,
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: `${COLORS.primary.main}10`,
                      borderColor: COLORS.primary.main,
                    }
                  }}
                >
                  Watch Demo
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip label="🌍 32 Languages" sx={{ backgroundColor: `${COLORS.primary.main}15`, color: COLORS.primary.main }} />
                <Chip label="💰 90% Cost Savings" sx={{ backgroundColor: `${COLORS.secondary.main}15`, color: COLORS.secondary.main }} />
                <Chip label="⚡ 10x Faster" sx={{ backgroundColor: `${COLORS.primary.light}15`, color: COLORS.primary.light }} />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center', position: 'relative' }}>
                <VideoLibrary sx={{ fontSize: 200, color: COLORS.primary.main, opacity: 0.3 }} />
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    background: COLORS.gradients.primary,
                    borderRadius: '50%',
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translate(-50%, -50%) scale(1.1)',
                    }
                  }}
                >
                  <PlayCircleOutline sx={{ fontSize: 50, color: 'white' }} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <StatsCard>
                <Typography variant="h3" sx={{ fontWeight: 700, color: stat.color, mb: 1 }}>
                  {stat.number}
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.text.secondary, fontWeight: 600 }}>
                  {stat.label}
                </Typography>
              </StatsCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h2"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            color: COLORS.text.primary,
            mb: 6,
            fontSize: { xs: '2rem', md: '2.5rem' }
          }}
        >
          Why Choose DubLabs AI Video Dubbing?
        </Typography>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <FeatureCard>
                <CardContent sx={{ p: 3, textAlign: 'left' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {feature.icon}
                    <Typography variant="h6" sx={{ ml: 2, fontWeight: 600, color: COLORS.text.primary }}>
                      {feature.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: COLORS.text.secondary, mb: 2, lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                  <List dense>
                    {feature.details.map((detail, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CheckCircle sx={{ fontSize: 16, color: COLORS.primary.main }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={detail} 
                          primaryTypographyProps={{ 
                            variant: 'body2', 
                            sx: { color: COLORS.text.tertiary, fontSize: '0.9rem' } 
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ backgroundColor: COLORS.background.secondary, py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: COLORS.text.primary,
              mb: 6,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            Transform Your Content Strategy
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                background: COLORS.gradients.primary, 
                borderRadius: 3, 
                p: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <AutoAwesome sx={{ fontSize: 60, color: 'white', mb: 2 }} />
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
                  Ready to Go Global?
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', mb: 3, opacity: 0.9 }}>
                  Start dubbing your videos today and reach audiences worldwide with authentic, 
                  AI-powered voice translation.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGetStarted}
                  sx={{
                    backgroundColor: 'white',
                    color: COLORS.primary.main,
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 4,
                    alignSelf: 'flex-start',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  Get in Touch
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: COLORS.text.primary, mb: 3 }}>
                Everything You Need for Global Content
              </Typography>
              <Grid container spacing={2}>
                {benefits.map((benefit, index) => (
                  <Grid item xs={12} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ color: COLORS.primary.main, mr: 2 }} />
                      <Typography variant="body1" sx={{ color: COLORS.text.secondary }}>
                        {benefit}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default AIVideoDubbingPage;